import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { dashboardHandler } from '../DashboardHandler';
import type { GetStatisticsCommandResponse } from '../../../../Core/Api/Api';

interface UseDashboardDataReturn {
  statsData: GetStatisticsCommandResponse | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

export const useDashboardData = (): UseDashboardDataReturn => {
  const [statsData, setStatsData] = useState<GetStatisticsCommandResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const statistics = await dashboardHandler.getStatisticData(source.token);

        if (isMounted) {
          setStatsData(statistics);
        }
      } catch (err: any) {
        if (isMounted && !axios.isCancel(err)) {
          setError(err.message || 'Failed to load dashboard data');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      source.cancel('Component unmounted');
    };
  }, [retryCount]);

  const retry = useCallback(() => setRetryCount((c) => c + 1), []);

  return { statsData, loading, error, retry };
};
